/**
 * MedicationEditor — structured row editor for pet medications.
 *
 * Each medication has: name, dosage, frequency, notes.
 * The backing store serialises to/from the `medicationsJson` column on pets.
 */
import { Plus, Trash2 } from 'lucide-react';

export interface Medication {
  name: string;
  dosage: string;
  frequency: string;
  notes: string;
}

export function parseMedicationsJson(raw: string | null | undefined): Medication[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((m): m is Record<string, unknown> => typeof m === 'object' && m !== null)
        .map((m) => ({
          name: typeof m.name === 'string' ? m.name : '',
          dosage: typeof m.dosage === 'string' ? m.dosage : '',
          frequency: typeof m.frequency === 'string' ? m.frequency : '',
          notes: typeof m.notes === 'string' ? m.notes : '',
        }));
    }
  } catch {
    // Silently ignore parse errors
  }
  return [];
}

export function serializeMedicationsJson(meds: Medication[]): string | null {
  const filled = meds.filter((m) => m.name.trim());
  if (filled.length === 0) return null;
  return JSON.stringify(filled);
}

const inputCls =
  'h-8 w-full rounded border border-border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring/30 min-w-0';

interface MedicationEditorProps {
  value: Medication[];
  onChange: (value: Medication[]) => void;
}

export function MedicationEditor({ value, onChange }: MedicationEditorProps) {
  function addRow() {
    onChange([...value, { name: '', dosage: '', frequency: '', notes: '' }]);
  }

  function removeRow(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function updateRow(idx: number, field: keyof Medication, val: string) {
    onChange(value.map((m, i) => (i === idx ? { ...m, [field]: val } : m)));
  }

  if (value.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-secondary/20 px-4 py-3 text-center">
        <p className="mb-2 text-xs text-muted-foreground">No medications on record.</p>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium hover:bg-secondary press"
        >
          <Plus className="size-3" strokeWidth={2} />
          Add medication
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {value.map((med, idx) => (
        <div
          key={`med-${idx}`}
          className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 rounded-md border border-border bg-background px-3 py-2"
        >
          <div>
            <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
              Name
            </label>
            <input
              type="text"
              value={med.name}
              onChange={(e) => updateRow(idx, 'name', e.target.value)}
              placeholder="Metacam"
              className={inputCls}
              aria-label={`Medication ${idx + 1} name`}
            />
          </div>
          <div>
            <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
              Dosage
            </label>
            <input
              type="text"
              value={med.dosage}
              onChange={(e) => updateRow(idx, 'dosage', e.target.value)}
              placeholder="1 ml"
              className={inputCls}
              aria-label={`Medication ${idx + 1} dosage`}
            />
          </div>
          <div>
            <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
              Frequency
            </label>
            <input
              type="text"
              value={med.frequency}
              onChange={(e) => updateRow(idx, 'frequency', e.target.value)}
              placeholder="Daily"
              className={inputCls}
              aria-label={`Medication ${idx + 1} frequency`}
            />
          </div>
          <div className="flex items-end pb-0.5">
            <button
              type="button"
              onClick={() => removeRow(idx)}
              aria-label={`Remove medication ${idx + 1}`}
              className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive press"
            >
              <Trash2 className="size-4" strokeWidth={1.75} />
            </button>
          </div>
          {/* Notes spans full width */}
          <div className="col-span-4">
            <input
              type="text"
              value={med.notes}
              onChange={(e) => updateRow(idx, 'notes', e.target.value)}
              placeholder="Additional notes (e.g. with food)"
              className={`${inputCls} text-muted-foreground`}
              aria-label={`Medication ${idx + 1} notes`}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary press"
      >
        <Plus className="size-3" strokeWidth={2} />
        Add medication
      </button>
    </div>
  );
}
